import i18next from "i18next";
import detector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { spError, spLog } from "@eventr/common";
import { loadLocale } from "./locales";

const i18n = i18next.createInstance();
let isInitialized = false;

const storedLanguage = localStorage.getItem("selectedLanguage") ?? localStorage.getItem("i18nextLng") ?? undefined;
spLog("i18n storedLanguage: " + storedLanguage, ["i18n"]);

async function initializeI18n() {
  spLog("initializeI18n called, isInitialized: " + isInitialized, ["i18n"]);

  if (isInitialized) {
    spLog("i18n already initialized, returning existing instance", ["i18n"]);
    return i18n;
  }

  try {
    await i18n
      // Resolves each language to a lazily-imported JSON chunk on local disk. Replaces
      // i18next-http-backend, which was configured against /messages/{{lng}}.json — a
      // path nothing was ever served from, so every load 404'd and the app ran entirely
      // off the statically-imported bundles that used to sit in ./locales.
      .use(resourcesToBackend((language: string) => loadLocale(language)))
      .use(detector)
      .init({
        debug: import.meta.env.DEV,
        fallbackLng: "en-GB",
        lng: storedLanguage,
        defaultNS: "translation",
        // Without this, i18next asks the backend for both "de-DE" and "de", which map to
        // the same chunk. The fallback language is loaded regardless of this setting.
        load: "currentOnly",
        detection: {
          order: ["localStorage", "navigator", "htmlTag", "path", "subdomain"],
          caches: ["localStorage"],
          lookupQuerystring: "lng",
        },
      });

    isInitialized = true;
    spLog("i18n initialized successfully, lng: " + i18n.language + ", resolvedLng: " + i18n.resolvedLanguage, ["i18n"]);

    return i18n;
  } catch (error) {
    spError("i18n initialization failed", error);
    throw error;
  }
}

/**
 * Module-scope promise that begins i18n initialization eagerly as soon as this module
 * is imported. React 19's use() consumes it in App.tsx to suspend until translations
 * are ready, eliminating the race condition that occurred with fire-and-forget async
 * calls during render.
 */
const i18nPromise = initializeI18n();

export { i18nPromise, initializeI18n };
export default i18n;
